/**
 * 產品維修路由 — 報修單／維修品項／維修報告／簽收單／物料序號。
 *
 * 註冊方式：由 adminRoutes.jsx import 合併至 children（與 crmRoutes 同層）。
 * SPA path：`/product-repair/…`；UI 目錄仍 `crmMgmt/aftersales/`；API 仍 `/aftersales/…`。
 * hub／各列表父路由掛 read；不掛 create／edit／complete 按鈕級守衛。
 */
import {
  AFTERSALES_REPAIR_ITEM_READ_CODES,
  AFTERSALES_REPAIR_REPORT_READ_CODES,
  AFTERSALES_REPAIR_REQUEST_READ_CODES,
  AFTERSALES_SIGN_RECEIPT_READ_CODES,
  AFTERSALES_SN_PRODUCT_MAPPING_READ_CODES,
  PRODUCT_REPAIR_HUB_READ_CODES,
} from '#/config/permissions';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import ProductRepairHubPage from '#/modules/infra/pages/ProductRepairHubPage';
import RepairItemDetailPage from '#/modules/crmMgmt/aftersales/repairItem/pages/RepairItemDetailPage';
import RepairItemListPage from '#/modules/crmMgmt/aftersales/repairItem/pages/RepairItemListPage';
import CreateEditRepairRequestPage from '#/modules/crmMgmt/aftersales/repairRequest/pages/CreateEditRepairRequestPage';
import RepairRequestDetailPage from '#/modules/crmMgmt/aftersales/repairRequest/pages/RepairRequestDetailPage';
import RepairRequestListPage from '#/modules/crmMgmt/aftersales/repairRequest/pages/RepairRequestListPage';
import CreateEditRepairReportPage from '#/modules/crmMgmt/aftersales/repairReport/pages/CreateEditRepairReportPage';
import RepairReportDetailPage from '#/modules/crmMgmt/aftersales/repairReport/pages/RepairReportDetailPage';
import RepairReportListPage from '#/modules/crmMgmt/aftersales/repairReport/pages/RepairReportListPage';
import CreateEditSignReceiptPage from '#/modules/crmMgmt/aftersales/signReceipt/pages/CreateEditSignReceiptPage';
import SignReceiptDetailPage from '#/modules/crmMgmt/aftersales/signReceipt/pages/SignReceiptDetailPage';
import SignReceiptListPage from '#/modules/crmMgmt/aftersales/signReceipt/pages/SignReceiptListPage';
import CreateEditSnProductMappingPage from '#/modules/crmMgmt/aftersales/snProductMapping/pages/CreateEditSnProductMappingPage';
import SnProductMappingDetailPage from '#/modules/crmMgmt/aftersales/snProductMapping/pages/SnProductMappingDetailPage';
import SnProductMappingListPage from '#/modules/crmMgmt/aftersales/snProductMapping/pages/SnProductMappingListPage';

export const productRepairRoutes = [
  {
    path: 'product-repair',
    handle: { title: '產品維修' },
    element: (
      <RequirePermission codes={PRODUCT_REPAIR_HUB_READ_CODES} mode="any" />
    ),
    children: [
      {
        index: true,
        element: <ProductRepairHubPage />,
      },
      {
        path: 'repair-requests',
        handle: { title: '報修單' },
        element: (
          <RequirePermission codes={AFTERSALES_REPAIR_REQUEST_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <RepairRequestListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增報修單' },
            element: <CreateEditRepairRequestPage />,
          },
          {
            path: ':id/edit',
            handle: { title: '編輯報修單' },
            element: <CreateEditRepairRequestPage />,
          },
          {
            path: ':id',
            handle: { title: '報修單詳情' },
            element: <RepairRequestDetailPage />,
          },
        ],
      },
      {
        path: 'repair-items',
        handle: { title: '維修品項' },
        element: (
          <RequirePermission codes={AFTERSALES_REPAIR_ITEM_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <RepairItemListPage />,
          },
          {
            path: ':id',
            handle: { title: '維修品項詳情' },
            element: <RepairItemDetailPage />,
          },
        ],
      },
      {
        path: 'repair-reports',
        handle: { title: '維修報告' },
        element: (
          <RequirePermission codes={AFTERSALES_REPAIR_REPORT_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <RepairReportListPage />,
          },
          {
            path: ':id/edit',
            handle: { title: '編輯維修報告' },
            element: <CreateEditRepairReportPage />,
          },
          {
            path: ':id',
            handle: { title: '維修報告詳情' },
            element: <RepairReportDetailPage />,
          },
        ],
      },
      {
        path: 'sign-receipts',
        handle: { title: '簽收單' },
        element: (
          <RequirePermission codes={AFTERSALES_SIGN_RECEIPT_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <SignReceiptListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增簽收單' },
            element: <CreateEditSignReceiptPage />,
          },
          {
            path: ':id/edit',
            handle: { title: '編輯簽收單' },
            element: <CreateEditSignReceiptPage />,
          },
          {
            path: ':id',
            handle: { title: '簽收單詳情' },
            element: <SignReceiptDetailPage />,
          },
        ],
      },
      {
        path: 'sn-product-mappings',
        handle: { title: '物料序號' },
        element: (
          <RequirePermission
            codes={AFTERSALES_SN_PRODUCT_MAPPING_READ_CODES}
            mode="any"
          />
        ),
        children: [
          {
            index: true,
            element: <SnProductMappingListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增 物料序號' },
            element: <CreateEditSnProductMappingPage />,
          },
          {
            path: ':id/edit',
            handle: { title: '編輯 物料序號' },
            element: <CreateEditSnProductMappingPage />,
          },
          {
            path: ':id',
            handle: { title: '物料序號詳情' },
            element: <SnProductMappingDetailPage />,
          },
        ],
      },
    ],
  },
];
